/**
 * report-icon-map.ts
 * Maps every report catalog id → Mi6 glyph name.
 * Glyph names must match keys exported from ./index.ts (minus "Icon" suffix).
 *
 * Glyph assignment rationale:
 *   pipeline-flow  → funnel/pipeline reports
 *   forecast       → forecast / accuracy / confidence
 *   coverage-health → coverage trend, CP coverage, health/hygiene
 *   activity-audit  → activity, audit, call quality, cadence completion
 *   decay-risk      → decayed accounts, concentration risk, at-risk
 *   velocity        → stage velocity, sales cycle length
 *   expansion-upsell → upsell, expansion, growth, won-lost
 *   quota-leaderboard → scoreboard, quota, team performance, leaderboard
 *   helix           → DNA / opportunity health / methodology adherence
 *   tower           → executive overview, strategic, top-of-house
 *   checkpoint-stack → CP coverage, question bank, discipline
 *   engagement      → BDR suspects/prospects, sourced cadence, content engagement
 */

export type GlyphName =
  | "pipeline-flow"
  | "forecast"
  | "coverage-health"
  | "activity-audit"
  | "decay-risk"
  | "velocity"
  | "expansion-upsell"
  | "quota-leaderboard"
  | "helix"
  | "tower"
  | "checkpoint-stack"
  | "engagement";

export const REPORT_ICON_MAP: Record<string, GlyphName> = {
  // ── AE — Pipeline & Forecast
  "ae-my-pipeline":                    "pipeline-flow",
  "ae-my-forecast":                    "forecast",
  "ae-forecast-accuracy":              "forecast",
  "ae-pipeline-coverage-trend":        "coverage-health",
  "ae-stage-velocity":                 "velocity",

  // ── AE — Account & Opportunity
  "ae-account-audit":                  "activity-audit",
  "ae-accounts-in-flight":             "checkpoint-stack",
  "ae-decayed-accounts":               "decay-risk",
  "ae-upsell-expansion-signal":        "expansion-upsell",
  "ae-won-lost-analysis":              "quota-leaderboard",

  // ── AE — Activity
  "ae-my-activity-audit":              "activity-audit",
  "ae-activity-vs-outcomes":           "velocity",
  "ae-discovery-call-quality":         "engagement",

  // ── AE — Discipline & Coaching
  "ae-cp-coverage-heatmap":            "checkpoint-stack",
  "ae-evidence-gap-report":            "coverage-health",
  "ae-coaching-history":               "helix",

  // ── AE — BDR Activity in My Book
  "ae-bdr-activity-in-my-accounts":    "engagement",
  "ae-suspect-prospect-conversion-my-book": "pipeline-flow",

  // ── BDR — Cadence & Throughput
  "bdr-my-cadence-queue":              "activity-audit",
  "bdr-cadence-completion-rate":       "velocity",
  "bdr-outbound-activity-audit":       "activity-audit",
  "bdr-connect-rate":                  "engagement",

  // ── BDR — Qualification & Pipeline Built
  "bdr-suspects-to-prospects":         "pipeline-flow",
  "bdr-prospects-handed-off":          "expansion-upsell",
  "bdr-my-hunt-list":                  "pipeline-flow",
  "bdr-icp-match-rate":                "coverage-health",

  // ── BDR — Source Attribution
  "bdr-source-funnel":                 "pipeline-flow",
  "bdr-source-attribution-multi-touch": "forecast",

  // ── BDR — Discipline
  "bdr-cadence-adherence-by-account":  "checkpoint-stack",
  "bdr-dial-time-audit":               "velocity",

  // ── Marketing — Funnel
  "mkt-marketing-funnel":              "pipeline-flow",
  "mkt-lead-source-performance":       "forecast",
  "mkt-conversion-rate-by-stage":      "velocity",

  // ── Marketing — Campaigns
  "mkt-campaign-performance":          "quota-leaderboard",
  "mkt-content-engagement":            "engagement",
  "mkt-event-pipeline-influence":      "expansion-upsell",

  // ── Marketing — Cadence & Activation
  "mkt-sourced-cadence-performance":   "activity-audit",
  "mkt-hooks-library-performance":     "engagement",

  // ── Manager — Team Performance
  "mgr-team-scoreboard":               "quota-leaderboard",
  "mgr-rep-detail-drilldown":          "activity-audit",
  "mgr-forecast-rollup":               "forecast",
  "mgr-composite-scoring":             "helix",

  // ── Manager — Discipline & Quality
  "mgr-data-hygiene":                  "coverage-health",
  "mgr-push-adequacy":                 "checkpoint-stack",
  "mgr-push-reentry":                  "decay-risk",
  "mgr-soft-stop-audit":               "decay-risk",
  "mgr-escalation-queue":              "decay-risk",
  "mgr-external-consultants":          "engagement",

  // ── Manager — Coaching
  "mgr-coaching-triggers-by-rep":      "helix",
  "mgr-discipline-defense-log":        "activity-audit",
  "mgr-1on1-prep-sheet":               "engagement",

  // ── Manager — Team Trends
  "mgr-weekly-movement-report":        "velocity",
  "mgr-stage-conversion-by-team":      "pipeline-flow",

  // ── Executive — Top-of-House
  "exec-executive-overview":           "tower",
  "exec-quarter-forecast-confidence":  "forecast",
  "exec-pipeline-trend-52-week":       "pipeline-flow",

  // ── Executive — Strategic
  "exec-industry-mix":                 "coverage-health",
  "exec-deal-size-distribution":       "quota-leaderboard",
  "exec-sales-cycle-length":           "velocity",
  "exec-customer-lifetime-value":      "expansion-upsell",

  // ── Executive — Risk
  "exec-concentration-risk":           "decay-risk",
  "exec-at-risk-forecast":             "decay-risk",

  // ── Cross-role / System-wide
  "cross-rep-quota-report":            "quota-leaderboard",
  "cross-question-bank-coverage":      "checkpoint-stack",
  "cross-methodology-adherence-score": "helix",
};

/** Fallback glyph when no mapping exists for a report id */
export const DEFAULT_GLYPH: GlyphName = "pipeline-flow";
